// src/components/TimePicker.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronDown, Clock3 } from "lucide-react";

function pad(n) {
    return String(n).padStart(2, "0");
}

function parseHHMM(v) {
    const m = typeof v === "string" && v.match(/^(\d{1,2}):(\d{1,2})$/);
    if (!m) return { h: "00", m: "00" };
    const H = Math.max(0, Math.min(23, parseInt(m[1], 10) || 0));
    const M = Math.max(0, Math.min(59, parseInt(m[2], 10) || 0));
    return { h: pad(H), m: pad(M) };
}

export default function TimePicker({
    value,            // "HH:MM" | undefined
    onChange,         // (next: "HH:MM") => void
    step = 15,        // minuter: 1 | 5 | 10 | 15 | 30
    placeholder = "HH:MM",
    disabled = false,
    className = "",
    triggerClassName = "",
    contentClassName = "",
    label,            // valfritt (renderas över listan)
}) {
    const [open, setOpen] = useState(false);
    const parsed = useMemo(() => parseHHMM(value), [value]);
    const [h, setH] = useState(parsed.h);
    const [m, setM] = useState(parsed.m);

    // Håll lokalt state i fas om parent ändrar value externt
    useEffect(() => { setH(parsed.h); setM(parsed.m); }, [parsed.h, parsed.m]);

    const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => pad(i)), []);
    const minutes = useMemo(() => {
        const s = Math.max(1, Math.min(60, step | 0));
        const out = [];
        for (let i = 0; i < 60; i += s) out.push(pad(i));
        return out;
    }, [step]);

    const apply = (hh = h, mm = m) => {
        const next = `${pad(+hh)}:${pad(+mm)}`;
        setH(hh); setM(mm);
        onChange?.(next);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className={`native-v3-time-trigger w-28 justify-between font-mono ${triggerClassName}`}
                    aria-label={label || "Välj tid"}
                >
                    <span className="native-v3-time-trigger-value"><Clock3 aria-hidden="true" />{value ? value : placeholder}</span>
                    <ChevronDown className="native-v3-time-trigger-chevron" aria-hidden="true" />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                align="start"
                className={`native-v3-time-popover ${contentClassName}`}
            >
                <div className="native-v3-time-popover-header">
                    <span><Clock3 aria-hidden="true" />{label || "Välj tid"}</span>
                    <strong>{h}:{m}</strong>
                </div>

                <div className="native-v3-time-columns">
                    {/* Hours */}
                    <div className="native-v3-time-column">
                      <span>Timme</span>
                      <ScrollArea className="native-v3-time-scroll">
                        <ul role="listbox" aria-label="Timmar">
                            {hours.map((hh) => {
                                const selected = hh === h;
                                return (
                                    <li key={hh}>
                                        <button
                                            type="button"
                                            className="native-v3-time-option"
                                            data-selected={selected ? "true" : "false"}
                                            aria-selected={selected}
                                            onClick={() => apply(hh, m)}
                                        >
                                            {hh}
                                            {selected ? <Check aria-hidden="true" /> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                      </ScrollArea>
                    </div>

                    {/* Minutes */}
                    <div className="native-v3-time-column">
                      <span>Minut</span>
                      <ScrollArea className="native-v3-time-scroll">
                        <ul role="listbox" aria-label="Minuter">
                            {minutes.map((mm) => {
                                const selected = mm === m;
                                return (
                                    <li key={mm}>
                                        <button
                                            type="button"
                                            className="native-v3-time-option"
                                            data-selected={selected ? "true" : "false"}
                                            aria-selected={selected}
                                            onClick={() => apply(h, mm)}
                                        >
                                            {mm}
                                            {selected ? <Check aria-hidden="true" /> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                      </ScrollArea>
                    </div>
                </div>

                <div className="native-v3-time-popover-footer">
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => { setOpen(false); }}
                    >
                        Klar
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
